import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { useEffect, useId, useState } from 'react';
import styled from 'styled-components';

import type { ModelInfo } from '@paradigm/terminal-core';

import {
  BASE_PICKER_SECTIONS,
  resolveBaseVersions,
  type BasePickerEntry,
} from '../../models/base-picker';
import {
  defaultModelLabel,
  pickDefaultModelId,
  resolveModelInCatalog,
} from '../../models/model-labels';
import { privacyFromTags } from '../../models/privacy';
import {
  formatVersionLabel,
  groupByModelAndVersion,
  groupModelsByCompany,
  type ModelVersionEntry,
  type ModelVersionGroup,
} from '../../models/provider-groups';
import { THIN_SCROLLBAR } from '../../styles/scrollbar';

interface Props {
  readonly models: readonly ModelInfo[];
  readonly selectedId: string | null;
  readonly onSelect: (id: string) => void;
}

/**
 * How long the loading label stays invisible. The catalog usually lands well
 * inside this (the sidecar caches it for 30s), and text that appears only to be
 * replaced a moment later reads as a glitch — so the common path is a silent
 * reserved slot, and only a genuinely slow gateway gets an explanation.
 */
const LOADING_LABEL_DELAY_MS = 500;

/**
 * Non-interactive stand-in that holds the picker's slot while the gateway's
 * catalog is in flight, or reports that it never arrived. Models come only
 * from `/api/models` (there is no built-in fallback list), so without this the
 * composer footer would collapse and then pop back in on every connect.
 *
 * The box is ALWAYS rendered at the real trigger's dimensions and hidden with
 * `visibility` rather than unmounted — that reserves the exact space the picker
 * will occupy, by construction rather than by a duplicated magic height, so
 * neither the delayed label nor the eventual swap moves anything on screen.
 */
export function ModelPickerPlaceholder({
  state,
}: {
  readonly state: 'loading' | 'unavailable';
}) {
  const [labelVisible, setLabelVisible] = useState(false);

  useEffect(() => {
    // `unavailable` is only reached after the retry budget is exhausted, so the
    // wait has already been long — show it immediately.
    if (state === 'unavailable') {
      setLabelVisible(true);
      return undefined;
    }
    const timer = setTimeout(
      () => setLabelVisible(true),
      LOADING_LABEL_DELAY_MS,
    );
    return () => clearTimeout(timer);
  }, [state]);

  const label = state === 'loading' ? 'Loading models…' : 'Models unavailable';
  return (
    // `visibility: hidden` also removes the node from the accessibility tree,
    // so the live region announces on reveal rather than at mount.
    <PlaceholderTrigger
      aria-live="polite"
      $visible={labelVisible}
      $muted={state === 'unavailable'}
    >
      {label}
    </PlaceholderTrigger>
  );
}

export default function ModelPicker({ models, selectedId, onSelect }: Props) {
  const menuId = useId();
  const [showAll, setShowAll] = useState(false);
  if (models.length === 0) return null;

  // What to NAME in the trigger. An id the catalog doesn't carry (a retired
  // gateway model, or a catalog that arrived in a different id shape) is
  // labelled from the id itself: falling back to models[0] would name the
  // session with a model it is not running, and since the catalog is sorted
  // by raw id, position 0 is arbitrary. With nothing selected yet, use the
  // same Opus-first default the provider's catalog effect applies.
  const matched = resolveModelInCatalog(models, selectedId);
  const displayId = matched?.id ?? selectedId ?? pickDefaultModelId(models);
  if (displayId == null) return null;
  const displayLabel = matched?.label ?? defaultModelLabel(displayId);

  return (
    <DropdownMenuPrimitive.Root
      onOpenChange={(open) => {
        if (!open) setShowAll(false);
      }}
    >
      <Trigger aria-label={`Model: ${displayLabel}`}>
        <TriggerLabel>{displayLabel}</TriggerLabel>
      </Trigger>
      <DropdownMenuPrimitive.Portal>
        <Content
          sideOffset={6}
          align="end"
          side="top"
          onCloseAutoFocus={(e) => {
            // Prevent Radix from returning focus to the trigger button —
            // redirect it to the chat textarea so the user can type
            // immediately after picking a model.
            e.preventDefault();
            const textarea = document.querySelector<HTMLTextAreaElement>(
              'textarea[data-chat-input]',
            );
            textarea?.focus();
          }}
        >
          {showAll ? (
            <FullList
              models={models}
              menuId={menuId}
              selectedId={displayId}
              onSelect={onSelect}
              onBack={() => setShowAll(false)}
            />
          ) : (
            <BaseList
              models={models}
              selectedId={displayId}
              onSelect={onSelect}
              onMore={() => setShowAll(true)}
            />
          )}
        </Content>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuPrimitive.Root>
  );
}

/** Curated base list + a "More Models →" affordance to open the full catalog. */
function BaseList({
  models,
  selectedId,
  onSelect,
  onMore,
}: {
  readonly models: readonly ModelInfo[];
  readonly selectedId: string;
  readonly onSelect: (id: string) => void;
  readonly onMore: () => void;
}) {
  const sections = BASE_PICKER_SECTIONS.map((section) =>
    section.filter((e) => resolveBaseVersions(models, e).length > 0),
  ).filter((section) => section.length > 0);

  return (
    <>
      {sections.map((section, si) => (
        <Group
          key={section.map((e) => e.label).join(',')}
          aria-labelledby={si === 0 ? 'base-heading' : undefined}
        >
          {si === 0 && <Heading id="base-heading">Models</Heading>}
          {section.map((entry) => (
            <BaseEntry
              key={entry.label}
              entry={entry}
              models={models}
              selectedId={selectedId}
              onSelect={onSelect}
            />
          ))}
        </Group>
      ))}
      <Group>
        <Item
          onSelect={(e) => {
            e.preventDefault();
            onMore();
          }}
        >
          <ItemLabel>More Models</ItemLabel>
          <CaretSlot>
            <CaretIcon />
          </CaretSlot>
        </Item>
      </Group>
    </>
  );
}

/** One curated entry — flat when it resolves to a single model, else a flyout. */
function BaseEntry({
  entry,
  models,
  selectedId,
  onSelect,
}: {
  readonly entry: BasePickerEntry;
  readonly models: readonly ModelInfo[];
  readonly selectedId: string;
  readonly onSelect: (id: string) => void;
}) {
  const versions = resolveBaseVersions(models, entry);
  if (versions.length === 0) return null;

  if (versions.length === 1) {
    const v = versions[0]!;
    return (
      <Item onSelect={() => onSelect(v.id)}>
        <ItemLabel>
          {entry.label}
          <PrivacyBadge tags={v.tags} />
        </ItemLabel>
        <CheckSlot>{v.id === selectedId && <CheckIcon />}</CheckSlot>
      </Item>
    );
  }

  const multipleProviders =
    new Set(versions.map((v) => v.parsed.provider)).size > 1;
  return (
    <VersionFlyout
      label={entry.label}
      versions={versions}
      multipleProviders={multipleProviders}
      selectedId={selectedId}
      onSelect={onSelect}
    />
  );
}

/** Full catalog, grouped by company — the "More Models" view. */
function FullList({
  models,
  menuId,
  selectedId,
  onSelect,
  onBack,
}: {
  readonly models: readonly ModelInfo[];
  readonly menuId: string;
  readonly selectedId: string;
  readonly onSelect: (id: string) => void;
  readonly onBack: () => void;
}) {
  const groups = groupModelsByCompany(models);
  return (
    <>
      <Group>
        <BackItem
          onSelect={(e) => {
            e.preventDefault();
            onBack();
          }}
        >
          ‹ Models
        </BackItem>
      </Group>
      {groups.map((group, gi) => {
        const headingId = `${menuId}-group-${gi}`;
        const versionGroups = groupByModelAndVersion(group.models);
        return (
          <Group key={group.label} aria-labelledby={headingId}>
            <Heading id={headingId}>{group.label}</Heading>
            {versionGroups.map((vg) => (
              <ModelEntry
                key={vg.modelName}
                vg={vg}
                selectedId={selectedId}
                onSelect={onSelect}
              />
            ))}
          </Group>
        );
      })}
    </>
  );
}

/** A full-catalog model entry — flat item or a version flyout. */
function ModelEntry({
  vg,
  selectedId,
  onSelect,
}: {
  readonly vg: ModelVersionGroup;
  readonly selectedId: string;
  readonly onSelect: (id: string) => void;
}) {
  const sorted = vg.versions;
  if (sorted.length === 1) {
    const v = sorted[0]!;
    return (
      <Item onSelect={() => onSelect(v.id)}>
        <ItemLabel>
          {formatVersionLabel(v.parsed)}
          {v.legacy === true && <LegacyTag>Legacy</LegacyTag>}
          <PrivacyBadge tags={v.tags} />
        </ItemLabel>
        <CheckSlot>{v.id === selectedId && <CheckIcon />}</CheckSlot>
      </Item>
    );
  }
  return (
    <VersionFlyout
      label={vg.modelName}
      versions={sorted}
      multipleProviders={vg.hasMultipleProviders}
      selectedId={selectedId}
      onSelect={onSelect}
    />
  );
}

/**
 * A model with multiple versions/providers: a Radix Sub whose trigger carries
 * NO privacy badge — the tier can differ per provider, so it's shown only on
 * the expanded entries.
 */
function VersionFlyout({
  label,
  versions,
  multipleProviders,
  selectedId,
  onSelect,
}: {
  readonly label: string;
  readonly versions: readonly ModelVersionEntry[];
  readonly multipleProviders: boolean;
  readonly selectedId: string;
  readonly onSelect: (id: string) => void;
}) {
  const anySelected = versions.some((v) => v.id === selectedId);
  return (
    <DropdownMenuPrimitive.Sub>
      <SubTrigger>
        <ItemLabel>
          {label}
          {anySelected && <SelectedDot />}
        </ItemLabel>
        <CaretSlot>
          <CaretIcon />
        </CaretSlot>
      </SubTrigger>
      <DropdownMenuPrimitive.Portal>
        <SubContent sideOffset={4}>
          {versions.map((v) => (
            <Item key={v.id} onSelect={() => onSelect(v.id)}>
              <ItemLabel>
                {formatVersionLabel(v.parsed)}
                {v.legacy === true && <LegacyTag>Legacy</LegacyTag>}
                {multipleProviders && (
                  <ProviderTag>{v.parsed.provider}</ProviderTag>
                )}
                <PrivacyBadge tags={v.tags} />
              </ItemLabel>
              <CheckSlot>{v.id === selectedId && <CheckIcon />}</CheckSlot>
            </Item>
          ))}
        </SubContent>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuPrimitive.Sub>
  );
}

function PrivacyBadge({ tags }: { readonly tags?: readonly string[] }) {
  const meta = privacyFromTags(tags);
  if (meta == null) return null;
  return (
    <Badge
      data-tier={meta.tier ?? undefined}
      title={meta.definition.length > 0 ? meta.definition : undefined}
    >
      {meta.label}
    </Badge>
  );
}

function CheckIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
      <path
        d="M5 12L10 17L20 7"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CaretIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
      <path
        d="M9 6L15 12L9 18"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ── Styled Components ──────────────────────────────────────────────────

const Trigger = styled(DropdownMenuPrimitive.Trigger)`
  all: unset;
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 400;
  color: var(--text-label);
  cursor: pointer;

  &:hover,
  &[data-state='open'] {
    color: var(--text-primary);
    background-color: var(--hover-bg);
  }
`;

const TriggerLabel = styled.span`
  white-space: nowrap;
`;

// Mirrors `Trigger`'s box so swapping in the real picker causes no reflow —
// minus the interactive affordances (no pointer cursor, no hover state).
// `visibility` (not `display`/unmount) keeps the box in layout while hidden,
// which is what reserves the row's height before the label is revealed.
const PlaceholderTrigger = styled.span<{
  readonly $visible: boolean;
  readonly $muted: boolean;
}>`
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 400;
  white-space: nowrap;
  color: var(--text-label);
  cursor: default;
  user-select: none;
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
  opacity: ${({ $muted }) => ($muted ? 0.6 : 1)};
`;

const Content = styled(DropdownMenuPrimitive.Content)`
  min-width: 220px;
  max-height: var(--radix-dropdown-menu-content-available-height);
  overflow-y: auto;
  overscroll-behavior: none;
  padding: 6px;
  border: 1px solid var(--border-color);
  background-color: var(--bg-color-container);
  border-radius: 8px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.32);
  z-index: 2000;
  ${THIN_SCROLLBAR}
`;

const SubContent = styled(DropdownMenuPrimitive.SubContent)`
  min-width: 160px;
  max-height: 300px;
  overflow-y: auto;
  overscroll-behavior: none;
  padding: 6px;
  border: 1px solid var(--border-color);
  background-color: var(--bg-color-container);
  border-radius: 8px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.32);
  z-index: 2001;
  ${THIN_SCROLLBAR}
`;

const Group = styled(DropdownMenuPrimitive.Group)`
  & + & {
    margin-top: 6px;
    padding-top: 6px;
    border-top: 1px solid var(--border-color);
  }
`;

const Heading = styled(DropdownMenuPrimitive.Label)`
  padding: 6px 8px 4px;
  font-size: 0.7rem;
  color: var(--text-label);
`;

const Item = styled(DropdownMenuPrimitive.Item)`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 16px;
  align-items: center;
  gap: 8px;
  height: 28px;
  padding: 0 8px 0 16px;
  border-radius: 4px;
  font-size: 0.8rem;
  color: var(--text-primary);
  cursor: pointer;
  outline: none;
  user-select: none;

  &[data-highlighted],
  &:hover {
    background-color: var(--hover-bg);
  }
`;

const BackItem = styled(DropdownMenuPrimitive.Item)`
  height: 28px;
  padding: 0 8px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  font-size: 0.75rem;
  color: var(--text-label);
  cursor: pointer;
  outline: none;
  user-select: none;

  &[data-highlighted],
  &:hover {
    color: var(--text-primary);
    background-color: var(--hover-bg);
  }
`;

const SubTrigger = styled(DropdownMenuPrimitive.SubTrigger)`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 16px;
  align-items: center;
  gap: 8px;
  height: 28px;
  padding: 0 8px 0 16px;
  border-radius: 4px;
  font-size: 0.8rem;
  color: var(--text-primary);
  cursor: pointer;
  outline: none;
  user-select: none;

  &[data-highlighted],
  &[data-state='open'],
  &:hover {
    background-color: var(--hover-bg);
  }
`;

const ItemLabel = styled.span`
  display: flex;
  align-items: baseline;
  gap: 6px;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LegacyTag = styled.span`
  font-size: 0.7rem;
  color: var(--text-label);
`;

const ProviderTag = styled.span`
  font-size: 0.65rem;
  padding: 0 4px;
  border-radius: 3px;
  background: var(--hover-bg);
  color: var(--text-label);
`;

const Badge = styled.span`
  margin-left: auto;
  font-size: 0.6rem;
  font-weight: 500;
  letter-spacing: 0.02em;
  padding: 1px 5px;
  border-radius: 3px;
  cursor: help;
  flex-shrink: 0;
  /* Neutral default for privacy labels with no known tier. */
  color: var(--text-label);
  background: var(--hover-bg);

  &[data-tier='tee'] {
    color: #1a7f37;
    background: rgba(26, 127, 55, 0.14);
  }
  &[data-tier='private'] {
    color: #2f6feb;
    background: rgba(47, 111, 235, 0.14);
  }
  &[data-tier='anon'] {
    color: #bb8009;
    background: rgba(187, 128, 9, 0.16);
  }
`;

const CheckSlot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--text-primary);
`;

const CaretSlot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--text-label);
`;

const SelectedDot = styled.span`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--text-primary);
  flex-shrink: 0;
`;
